import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset, GlobalAssumptions } from '@/types/GraphqlSchema';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'financial_considerations_table';

const FINANCIAL_CONSIDERATIONS_QUERY = gql`
  query assetFinancialConsiderations($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      formattedNpv
      formattedNpvBoe
      formattedRemainingCapex
      formattedOpexBoe
      fiscalRegime
    }
    globalAssumptions {
      baseYear
      discountRate
    }
  }
`;

interface FinancialConsiderationsData {
  npv: string;
  discountRate: string;
  fiscalRegime: string;
  remainingCapex: string;
  opexPerBarrel: string;
  npvPerBarrel: string;
  baseYear: string;
}

const formatAssetFinancialConsiderations = (
  asset: Asset,
  globalAssumptions: GlobalAssumptions,
): FinancialConsiderationsData => {
  const baseYear = globalAssumptions.baseYear || new Date().getFullYear().toString();

  return {
    npv: asset.formattedNpv ? `$${asset.formattedNpv}` : 'N/A',
    discountRate: globalAssumptions.discountRate ? `${globalAssumptions.discountRate}%` : 'N/A',
    fiscalRegime: asset.fiscalRegime || 'N/A',
    remainingCapex: asset.formattedRemainingCapex ? `$${asset.formattedRemainingCapex}` : 'N/A',
    opexPerBarrel: asset.formattedOpexBoe ? `$${asset.formattedOpexBoe}` : 'N/A',
    npvPerBarrel: asset.formattedNpvBoe ? `$${asset.formattedNpvBoe}` : 'N/A',
    baseYear,
  };
};

const useData = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: UseDataType<FinancialConsiderationsData>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const { loading, error } = useQuery<{
    assetV2: Asset;
    globalAssumptions: GlobalAssumptions;
  }>(FINANCIAL_CONSIDERATIONS_QUERY, {
    variables: { input: { id: assetId } },
    skip: !!persistedData || !assetId,
    onCompleted: response => {
      if (response?.assetV2 && response?.globalAssumptions) {
        const data = formatAssetFinancialConsiderations(
          response.assetV2,
          response.globalAssumptions,
        );
        setDataFetched(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<FinancialConsiderationsData>) => {
  if (!data) return null;

  const rows = [
    { label: 'NPV (US$MM)', value: data.npv },
    { label: 'Discount Rate', value: data.discountRate },
    { label: 'Fiscal Regime', value: data.fiscalRegime },
    { label: 'Remaining Capex + Abex (US$MM)', value: data.remainingCapex },
    {
      label: `${data.baseYear} Opex per Barrel (US$/boe)`,
      value: data.opexPerBarrel,
    },
    { label: 'NPV per Barrel of Reserves (US$/boe)', value: data.npvPerBarrel },
  ];

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
      data-testid='financial-considerations-table'
    >
      <ComponentTable>
        <ComponentTableHeader>
          Financial Considerations Base Year - {data.baseYear}
        </ComponentTableHeader>
        <ComponentTableBody rows={rows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<FinancialConsiderationsData>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <ComponentView data={data} />}
    </LoadingErrorWrapper>
  );
};

export const FinancialConsiderationsTableComponent: ComponentDefinition<FinancialConsiderationsData> =
  {
    id: COMPONENT_ID,
    name: 'Financial Considerations',
    type: SmartReportComponentTypesEnum.Table,
    reportTypes: [SmartReportTypeEnum.Asset],
    Component: ComponentEdit,
    ComponentView,
  };
